import { useState, useCallback, useMemo } from 'react';
import { InvestorDocument } from '@/types/investor';
import { DocumentService } from '@/services/documentService';
import { InvestorService } from '@/services/investorService';
import { createComponentLogger } from '@/utils/logger';

const log = createComponentLogger('useInvestorDocuments', 'investor-documents');

export interface DocumentGroup {
  groupKey: string;
  category: string;
  subType?: string;
  documents: InvestorDocument[]; // Active docs, sorted by version desc
}

interface UseInvestorDocumentsResult {
  /** Active documents for the current channel */
  documents: InvestorDocument[];
  /** Documents grouped by category + subType */
  groupedDocuments: DocumentGroup[];
  /** Loading state for initial document fetch */
  isLoading: boolean;
  /** Reload documents from the database */
  reload: () => Promise<void>;
  /** Version history for a specific category (loaded on demand) */
  versionHistory: Record<string, InvestorDocument[]>;
  /** Whether version history is loading for a specific group */
  versionHistoryLoading: Record<string, boolean>;
  /** Load version history (all versions including archived) for a category */
  loadVersionHistory: (groupKey: string, category: string, subType?: string) => Promise<void>;
  /** Set of group keys with expanded version history */
  expandedGroups: Set<string>;
  /** Toggle version history expansion for a group */
  toggleExpanded: (groupKey: string, category: string, subType?: string) => void;
}

export function useInvestorDocuments(
  investorId: string,
  documentType: 'wholesale' | 'correspondent'
): UseInvestorDocumentsResult {
  const [documents, setDocuments] = useState<InvestorDocument[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [versionHistory, setVersionHistory] = useState<Record<string, InvestorDocument[]>>({});
  const [versionHistoryLoading, setVersionHistoryLoading] = useState<Record<string, boolean>>({});
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const reload = useCallback(async () => {
    setIsLoading(true);
    try {
      const docs = await InvestorService.getInvestorDocuments(
        investorId,
        documentType,
        true // Active only
      );
      setDocuments(docs);
    } catch (error) {
      log.error('Failed to load documents', { error });
      setDocuments([]);
    } finally {
      setIsLoading(false);
    }
  }, [investorId, documentType]);

  // Group documents by category + subType
  const groupedDocuments = useMemo((): DocumentGroup[] => {
    const groups: Record<string, { category: string; subType?: string; docs: InvestorDocument[] }> = {};

    for (const doc of documents) {
      // Item 10: group by lineage (document_group_id) so multiple documents of the same type
      // are separate rows. Fall back to category for any legacy row without a lineage id.
      const groupKey = doc.documentGroupId ?? doc.category;

      if (!groups[groupKey]) {
        groups[groupKey] = { category: doc.category, subType: doc.subType, docs: [] };
      }
      groups[groupKey].docs.push(doc);
    }

    // Sort docs within each group by version descending
    return Object.entries(groups).map(([groupKey, group]) => ({
      groupKey,
      category: group.category,
      subType: group.subType,
      documents: [...group.docs].sort((a, b) => b.version - a.version)
    }));
  }, [documents]);

  const loadVersionHistory = useCallback(async (groupKey: string, _category: string, _subType?: string) => {
    setVersionHistoryLoading(prev => ({ ...prev, [groupKey]: true }));
    try {
      // groupKey is the lineage id (document_group_id) — version history is per-lineage (item 10).
      const history = await DocumentService.getDocumentVersionHistory(groupKey);
      setVersionHistory(prev => ({ ...prev, [groupKey]: history }));
    } catch (error) {
      log.error('Failed to load version history', { error, groupKey });
    } finally {
      setVersionHistoryLoading(prev => ({ ...prev, [groupKey]: false }));
    }
  }, []);

  const toggleExpanded = useCallback((groupKey: string, category: string, subType?: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupKey)) {
        next.delete(groupKey);
      } else {
        next.add(groupKey);
        // Load version history on first expand
        if (!versionHistory[groupKey]) {
          loadVersionHistory(groupKey, category, subType);
        }
      }
      return next;
    });
  }, [versionHistory, loadVersionHistory]);

  return {
    documents,
    groupedDocuments,
    isLoading,
    reload,
    versionHistory,
    versionHistoryLoading,
    loadVersionHistory,
    expandedGroups,
    toggleExpanded
  };
}
